/**
 * Offline mutation queue.
 *
 * Minimal IndexedDB-backed store for POST/PUT/DELETE requests that fail
 * because the browser is offline. When the `online` event fires the
 * queue is flushed FIFO; each entry replays exactly once. Failures during
 * replay bump an attempt counter and are dropped after 3 tries so a
 * permanently-broken request can't block the queue indefinitely.
 *
 * This is deliberately request-level (not mutation-aware). React Query's
 * onMutate optimistic updates already show the change in the UI; the
 * queue's job is only to eventually get the server to the same state.
 * On successful replay we invalidate the `app` query so fresh data pulls.
 */

const DB_NAME = "rc-offline";
const DB_VERSION = 1;
const STORE_NAME = "mutations";
const MAX_ATTEMPTS = 3;

export interface QueuedMutation {
  id: number;
  method: string;
  url: string;
  body: string | null;
  enqueuedAt: number;
  attempts: number;
}

export class OfflineQueuedError extends Error {
  constructor(public readonly queueId: number) {
    super("Request queued for offline replay");
    this.name = "OfflineQueuedError";
  }
}

const listeners = new Set<(size: number) => void>();

function canUseIndexedDb(): boolean {
  return typeof indexedDB !== "undefined";
}

function openDb(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    if (!canUseIndexedDb()) {
      reject(new Error("IndexedDB not available"));
      return;
    }
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        db.createObjectStore(STORE_NAME, {
          keyPath: "id",
          autoIncrement: true,
        });
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

async function withStore<T>(
  mode: IDBTransactionMode,
  op: (store: IDBObjectStore) => IDBRequest<T> | Promise<T>,
): Promise<T> {
  const db = await openDb();
  return new Promise<T>((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, mode);
    const store = tx.objectStore(STORE_NAME);
    const result = op(store);

    if (result instanceof IDBRequest) {
      result.onsuccess = () => resolve(result.result as T);
      result.onerror = () => reject(result.error);
    } else {
      Promise.resolve(result).then(resolve, reject);
    }

    tx.onerror = () => reject(tx.error);
    tx.onabort = () => reject(tx.error ?? new Error("Transaction aborted"));
  });
}

export async function enqueueMutation(params: {
  method: string;
  url: string;
  body: string | null;
}): Promise<number> {
  if (!canUseIndexedDb()) {
    throw new Error("Offline queue unavailable in this browser");
  }
  const entry: Omit<QueuedMutation, "id"> = {
    method: params.method,
    url: params.url,
    body: params.body,
    enqueuedAt: Date.now(),
    attempts: 0,
  };
  const id = await withStore("readwrite", (store) => store.add(entry));
  notifyListeners();
  return id as unknown as number;
}

export async function getQueueSize(): Promise<number> {
  if (!canUseIndexedDb()) return 0;
  return await withStore("readonly", (store) => store.count());
}

async function listQueue(): Promise<QueuedMutation[]> {
  if (!canUseIndexedDb()) return [];
  return await withStore("readonly", (store) => store.getAll()) as QueuedMutation[];
}

async function deleteEntry(id: number): Promise<void> {
  await withStore("readwrite", (store) => store.delete(id));
}

async function updateEntry(entry: QueuedMutation): Promise<void> {
  await withStore("readwrite", (store) => store.put(entry));
}

let flushInFlight = false;

export async function flushQueue(): Promise<{
  sent: number;
  failed: number;
  dropped: number;
}> {
  if (!canUseIndexedDb()) return { sent: 0, failed: 0, dropped: 0 };
  if (flushInFlight) return { sent: 0, failed: 0, dropped: 0 };
  flushInFlight = true;

  let sent = 0;
  let failed = 0;
  let dropped = 0;

  try {
    const entries = await listQueue();
    entries.sort((a, b) => a.enqueuedAt - b.enqueuedAt);

    for (const entry of entries) {
      try {
        const res = await fetch(entry.url, {
          method: entry.method,
          headers: { "Content-Type": "application/json" },
          body: entry.body,
          credentials: "same-origin",
        });
        if (res.ok) {
          await deleteEntry(entry.id);
          sent += 1;
        } else {
          // Server rejected — bump attempts; drop after MAX_ATTEMPTS
          const nextAttempts = entry.attempts + 1;
          if (nextAttempts >= MAX_ATTEMPTS) {
            await deleteEntry(entry.id);
            dropped += 1;
          } else {
            await updateEntry({ ...entry, attempts: nextAttempts });
            failed += 1;
          }
        }
      } catch {
        // Network error — stop the flush; stays queued for the next try
        failed += 1;
        break;
      }
    }
  } finally {
    flushInFlight = false;
    notifyListeners();
  }

  return { sent, failed, dropped };
}

function notifyListeners(): void {
  if (listeners.size === 0) return;
  void getQueueSize().then((size) => {
    for (const listener of listeners) {
      try {
        listener(size);
      } catch {
        // non-fatal
      }
    }
  });
}

export function subscribeQueueSize(
  listener: (size: number) => void,
): () => void {
  listeners.add(listener);
  void getQueueSize().then(listener);
  return () => {
    listeners.delete(listener);
  };
}

/**
 * Wire up the online event so the queue flushes as soon as connectivity
 * is restored. Call this once at app init.
 */
export function installOnlineFlush(): void {
  if (typeof window === "undefined") return;
  window.addEventListener("online", () => {
    void flushQueue();
  });
}
